import React, { useEffect, useRef, useMemo } from "react";
import {
  Box,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useSelector } from "react-redux";
import { RootState } from "../../../../redux/store";
import JsonView from '@microlink/react-json-view';

interface CortexModalProps {
  open: boolean;
  onClose: () => void;
  conversationId?: string;
  chatSessionId?: string;
}

/* ────────────────────────────────────────────────────────── */
/* helpers                                                    */
/* ────────────────────────────────────────────────────────── */

/**
 * Transform state data to show field_name=value pairs at the top level
 * Matches how live_brain.py renders behaviors with StateSpace, CData, and persistent data
 */
const transformStateData = (state: any) => {
  if (!state) return state;

  const transformed: any = {};

  // Extract StateSpace fields directly (like live_brain.py lines 263-268)
  if (state.state && state.state.data && state.state.schema?.field_lookup) {
    const fieldLookup = state.state.schema.field_lookup;
    const data = state.state.data;

    Object.keys(fieldLookup).forEach((offset: string) => {
      const field = fieldLookup[offset];
      const value = data[parseInt(offset)];
      if (field?.name !== undefined && value !== undefined) {
        transformed[field.name] = value;
      }
    });
  }

  // Add CData directly (like live_brain.py lines 269-275)
  if (state.data?.cdata_root) {
    transformed['_cdata'] = state.data.cdata_root;
  }

  // Add persisted_data directly (like live_brain.py lines 277-282)
  if (state.data?.persisted_data) {
    transformed['_persisted'] = state.data.persisted_data;
  }

  // If we didn't extract anything useful, return the original state
  return Object.keys(transformed).length > 0 ? transformed : state;
};

/**
 * Transform states dict to show each behavior's state more clearly
 */
const transformStatesDict = (states: any) => {
  if (!states || typeof states !== 'object') return states;

  const transformedStates: any = {};
  Object.keys(states).forEach((behaviorId: string) => {
    const state = states[behaviorId];
    transformedStates[behaviorId] = transformStateData(state);
  });

  return transformedStates;
};

/**
 * Transform datum dict to flatten cdata_root values directly under each behavior
 */
const transformDatumDict = (datum: any) => {
  if (!datum || typeof datum !== 'object') return datum;

  const transformedDatum: any = {};
  Object.keys(datum).forEach((behaviorId: string) => {
    const behaviorData = datum[behaviorId];

    // Flatten: datum.behavior_id.data.cdata_root -> datum.behavior_id
    if (behaviorData?.data?.cdata_root) {
      transformedDatum[behaviorId] = behaviorData.data.cdata_root;
    } else if (behaviorData?.data) {
      transformedDatum[behaviorId] = behaviorData.data;
    } else {
      transformedDatum[behaviorId] = behaviorData;
    }
  });

  return transformedDatum;
};

const transformCortexDataForDisplay = (cortexData: any) => {
  if (!cortexData) return cortexData;

  try {
    const transformedData = { ...cortexData };

    // Transform history array into object with descriptive keys
    if (transformedData.history && Array.isArray(transformedData.history)) {
      const transformedHistory: any = {};
      transformedData.history.forEach((item: any, index: number) => {
        let keyName = `[${index}]`;
        if (item?.clock?.current_message_id) {
          keyName += ` MessageID: ${item.clock.current_message_id}`;
        }
        if (item?.chosen_action?.id) {
          keyName += ` ChosenAction: ${item.chosen_action.id}`;
        }

        // Transform states and datum dicts for this history item
        const transformedItem = { ...item };
        if (item.states) {
          transformedItem.states = transformStatesDict(item.states);
        }
        if (item.datum) {
          transformedItem.datum = transformDatumDict(item.datum);
        }

        transformedHistory[keyName] = transformedItem;
      });
      transformedData.history = transformedHistory;
    }

    // Transform current snapshot states and datum if present
    if (transformedData.states) {
      transformedData.states = transformStatesDict(transformedData.states);
    }
    if (transformedData.datum) {
      transformedData.datum = transformDatumDict(transformedData.datum);
    }

    return transformedData;
  } catch (error) {
    console.error('Error transforming cortex data:', error);
    // Return original data if transformation fails
    return cortexData;
  }
};

/* ────────────────────────────────────────────────────────── */
/* component                                                  */
/* ────────────────────────────────────────────────────────── */
const CortexModal: React.FC<CortexModalProps> = ({
  open,
  onClose,
  conversationId,
  chatSessionId,
}) => {
  /* — redux state — */
  const cortexData: any = useSelector((state: RootState) => {
    if (!conversationId || !chatSessionId) return undefined;
    const key = `${conversationId}-${chatSessionId}`;
    return state.conversations.cortexData?.[key];
  });

  const loadingCortex = useSelector((state: RootState) => {
    if (!conversationId || !chatSessionId) return false;
    const key = `${conversationId}-${chatSessionId}`;
    return state.conversations.loadingCortexData?.[key] || false;
  });

  /* — computed data — */
  const transformedCortexData = useMemo(() => {
    return transformCortexDataForDisplay(cortexData);
  }, [cortexData]);

  /* — refs / handlers — */
  const modalRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (open && modalRef.current) modalRef.current.focus();
  }, [open]);

  const blockCtrlF = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if ((e.ctrlKey || e.metaKey) && e.key === "f") e.stopPropagation();
  };

  /* — loading / empty states — */
  if (!conversationId || !chatSessionId || loadingCortex) {
    return (
      <Dialog open={open} onClose={onClose} fullWidth maxWidth="lg">
        <DialogTitle sx={{ display: "flex", justifyContent: "space-between" }}>
          Cortex State Viewer
          <IconButton onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Box display="flex" alignItems="center" justifyContent="center" p={4}>
            <CircularProgress />
            <Typography sx={{ ml: 2 }}>Loading cortex state…</Typography>
          </Box>
        </DialogContent>
      </Dialog>
    );
  }

  if (!cortexData) {
    return (
      <Dialog open={open} onClose={onClose} fullWidth maxWidth="lg">
        <DialogTitle sx={{ display: "flex", justifyContent: "space-between" }}>
          Cortex State Viewer
          <IconButton onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Typography>Unpickling error - No cortex state data available.</Typography>
        </DialogContent>
      </Dialog>
    );
  }

  /* — main render — */
  return (
    <Dialog
      ref={modalRef}
      onKeyDown={blockCtrlF}
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="lg"
    >
      <DialogTitle sx={{ display: "flex", justifyContent: "space-between" }}>
        Cortex State Viewer
        <IconButton onClick={onClose}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      {/* ───────────── CONTENT ───────────── */}
      <DialogContent dividers>
        <Box
          sx={{
            height: "72vh",
            overflowY: "auto",
            p: 2,
          }}
        >
          <Typography variant="h6" gutterBottom>
            Chat Session: {chatSessionId}
          </Typography>
          <Typography variant="subtitle2" color="textSecondary" gutterBottom>
            Conversation: {conversationId}
          </Typography>
          
          <Box sx={{ mt: 3 }}>
            <JsonView
              src={transformedCortexData}
              theme={{
                base00: '#1e1e1e',
                base01: '#2d2d2d',
                base02: '#393939',
                base03: '#515151',
                base04: '#b4b4b4',
                base05: '#e6e6e6',
                base06: '#f0f0f0',
                base07: '#ffffff',
                base08: '#ff6b6b',
                base09: '#ffa500',
                base0A: '#ffeb3b',
                base0B: '#4caf50',
                base0C: '#00bcd4',
                base0D: '#2196f3',
                base0E: '#9c27b0',
                base0F: '#795548'
              }}
              collapsed={2}
              displayDataTypes={false}
              displayObjectSize={true}
              enableClipboard={true}
              style={{
                fontFamily: 'monospace',
                fontSize: '13px',
                backgroundColor: '#1e1e1e',
                color: '#ffffff'
              }}
            />
          </Box>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default CortexModal;
